import React, { useState, useEffect } from 'react';
import { Check, X, ArrowRight, ChevronRight, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ChatFormOption {
  id: string;
  label: string;
  description?: string;
}

export interface InteractiveChatFormProps {
  title?: string;
  options: string[] | ChatFormOption[];
  multiple?: boolean;
  onSkip?: () => void;
  onSubmit: (values: string[]) => void;
  initialValues?: string[];
  isOpen?: boolean;
  onClose?: () => void;
}

export const InteractiveChatForm: React.FC<InteractiveChatFormProps> = ({
  title = "Selecciona una opción",
  options,
  multiple = true,
  onSkip,
  onSubmit,
  initialValues = [],
  isOpen = true,
  onClose
}) => {
  const [selected, setSelected] = useState<string[]>(initialValues);

  // Normalize options to ChatFormOption objects
  const normalizedOptions: ChatFormOption[] = options.map(opt => 
    typeof opt === 'string' ? { id: opt, label: opt } : opt
  );

  const handleToggle = (id: string) => {
    if (multiple) {
      setSelected(prev => 
        prev.includes(id) 
          ? prev.filter(item => item !== id) 
          : [...prev, id]
      );
    } else {
      setSelected([id]);
    }
  };

  const handleSubmit = () => {
    if (selected.length > 0 || !multiple) {
      onSubmit(selected);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="my-4 animate-in fade-in zoom-in-95 duration-300 max-w-md w-full">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden shadow-blue-900/5">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h3 className="text-sm font-bold text-slate-800 tracking-tight">{title}</h3>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Options List */}
        <div className="max-h-[300px] overflow-y-auto p-2 space-y-1">
          {normalizedOptions.map((option) => {
            const isSelected = selected.includes(option.id);
            return (
              <button
                key={option.id}
                onClick={() => handleToggle(option.id)}
                className={`w-full text-left px-3 py-3 rounded-xl transition-all flex items-start gap-3 group
                  ${isSelected 
                    ? 'bg-blue-50 border-transparent' 
                    : 'bg-transparent hover:bg-slate-50 border-transparent'
                  }`}
              >
                <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all
                  ${isSelected 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'bg-white border-slate-300 text-transparent group-hover:border-blue-400'
                  }`}
                >
                  <Check className={`h-3 w-3 ${isSelected ? 'opacity-100' : 'opacity-0'}`} strokeWidth={4} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold leading-tight ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>
                    {option.label}
                  </div>
                  {option.description && (
                    <div className={`text-xs mt-0.5 line-clamp-1 ${isSelected ? 'text-blue-600/70' : 'text-slate-400'}`}>
                      {option.description}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            {selected.length} {selected.length === 1 ? 'seleccionado' : 'seleccionados'}
          </div>
          
          <div className="flex items-center gap-2">
            {onSkip && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onSkip}
                className="text-slate-500 hover:text-slate-800 h-9 font-bold text-xs px-4"
              >
                Saltar
              </Button>
            )}
            <Button 
              size="sm" 
              disabled={selected.length === 0}
              onClick={handleSubmit}
              className={`h-9 px-4 rounded-lg font-bold text-xs gap-2 transition-all shadow-sm
                ${selected.length > 0 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200' 
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
            >
              Enviar
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={3} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
